import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Wifi, Smartphone, Phone, Network, Palette, Target, Globe, Code,
  ArrowRight, Sparkles, Wand2, Trash2,
} from "lucide-react";

const ICONS = {
  Internet: Wifi, Mobile: Smartphone, Voix: Phone, "Réseau": Network,
  Branding: Palette, Acquisition: Target, Web: Globe, "Développement": Code,
  IA: Sparkles, "Sur mesure": Wand2,
};

export default function Templates() {
  const navigate = useNavigate();
  const { role, isSuperadmin, brand } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [gen, setGen] = useState({ template_name: "", brief: "" });
  const [generating, setGenerating] = useState(false);

  const canManage = ["owner", "admin"].includes(role) || isSuperadmin;

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const applyTemplate = async (t) => {
    const sections = t.sections.map((s) => ({
      type: s.type, title: s.title || "", content: s.content || "",
      items: (s.items || []).map((it) => ({ ...it })),
    }));
    const { data } = await api.post("/proposals", { title: t.name, sections, currency: "EUR" });
    toast.success("Proposition créée à partir du modèle");
    navigate(`/proposals/${data.id}/edit`);
  };

  const generate = async () => {
    if (!gen.template_name.trim()) return toast.error("Donnez un nom au modèle");
    setGenerating(true);
    try {
      await api.post("/ai/generate-template", { company_slug: brand.slug, name: gen.template_name, description: gen.brief });
      toast.success("Modèle généré par l'IA");
      setAiOpen(false);
      setGen({ template_name: "", brief: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce modèle ?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Modèle supprimé");
    load();
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Bibliothèque</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Modèles</h1>
          <p className="text-muted-foreground mt-2">Démarrez en quelques secondes avec des modèles prêts à closer.</p>
        </div>
        {canManage && (
          <Button onClick={() => setAiOpen(true)} data-testid="generate-template-btn" className="rounded-sm bg-foreground hover:bg-primary font-semibold h-11">
            <Sparkles className="w-4 h-4 mr-1" /> Générer un modèle par IA
          </Button>
        )}
      </div>

      {templates.length === 0 && (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center" data-testid="empty-templates">
          <Wand2 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Aucun modèle pour cette société.</p>
          {canManage && <p className="text-sm text-muted-foreground mt-1">Générez votre premier modèle cohérent avec votre secteur grâce à l'IA.</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((t) => {
          const Icon = ICONS[t.category] || Wand2;
          return (
            <div key={t.id} data-testid={`template-${t.id}`}
              className="group bg-card border border-border rounded-sm overflow-hidden transition-all duration-200 hover:border-primary/50 hover:-translate-y-0.5">
              <div className="h-32 flex items-center justify-center relative" style={{ backgroundColor: `${t.accent}10` }}>
                <Icon className="w-12 h-12" strokeWidth={1.5} style={{ color: t.accent }} />
                <span className="absolute top-3 left-3 text-[11px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm"
                  style={{ backgroundColor: t.accent, color: "#fff" }}>{t.category}</span>
                {t.custom && canManage && (
                  <button onClick={(e) => remove(t.id, e)} data-testid={`delete-template-${t.id}`}
                    className="absolute top-2 right-2 p-1.5 rounded-sm bg-white/80 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold mb-1">{t.name}</h3>
                <p className="text-sm text-muted-foreground mb-5 min-h-[40px] line-clamp-2">{t.description}</p>
                <button onClick={() => applyTemplate(t)} data-testid={`use-template-${t.id}`}
                  className="w-full flex items-center justify-center gap-2 bg-foreground text-white h-10 rounded-sm font-medium text-sm hover:bg-primary transition-all duration-200 group">
                  Utiliser ce modèle
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Générer un modèle pour {brand.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">L'IA crée un modèle complet (objectifs, processus, chiffres, tarification, FAQ) cohérent avec votre secteur.</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nom du modèle</Label><Input value={gen.template_name} onChange={(e) => setGen({ ...gen, template_name: e.target.value })} data-testid="gen-template-name" placeholder="Ex : Pack Visibilité 360°" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Brief / contexte (optionnel)</Label><Textarea value={gen.brief} onChange={(e) => setGen({ ...gen, brief: e.target.value })} data-testid="gen-template-brief" rows={3} placeholder="Décrivez l'offre, la cible, le positionnement…" className="mt-1.5 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button onClick={generate} disabled={generating} data-testid="gen-template-submit" className="rounded-sm bg-primary hover:bg-primary/90">
              {generating ? "Génération…" : <><Sparkles className="w-4 h-4 mr-1" /> Générer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
